import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ApolloError } from '@apollo/client';
import { useRecoilValue } from 'recoil';

import { Button } from 'components';
import { EmailSelectorInput } from 'components/EmailSelectorInput/EmailSelectorInput';
import { If } from 'components/Generics';
import ResponsiveModal from 'components/Modals/ResponsiveModal/ResponsiveModal';
import { EMAIL_REGEX } from 'constants/common';
import SalesforceEmailTemplatePickerModal from 'pages/Salesforce/SalesforceEmailTemplatePickerModal';
import {
  useGetRenderedTemplateById,
  useGetSalesforceOrgWideEmailAddresses,
  useSendSalesforceEmail
} from 'services/hooks/SalesforceAccounts.hooks';
import { GET_SALESFORCE_ACTIVITIES } from 'services/query/SalesforceLeads.queries';
import { loginedUserDetails } from 'store/atoms';
import {
  SalesforceEmailTemplate,
  SendSalesforceEmailResponse
} from 'types/SalesforceAccounts.types';
import { showToast } from 'utils/toast/toast.util';

import { SF_STRING_MAX_LENGTH } from './constants/fieldLimits';
import { stripHtml } from './utils/stripHtml';

interface SendEmailModalProps {
  open: boolean;
  relatedRecordId?: string;
  relatedEntityType: string;
  recipientId?: string;
  recipientEmail: string;
  onClose: () => void;
  onSent?: () => void;
  /** Shows the minimize button; Esc / overlay click minimize instead of closing. */
  onMinimize?: () => void;
}

const formatOrgWideAddressLabel = (
  displayName?: string,
  address?: string
): string => {
  if (!address) return '';
  return displayName ? `${displayName} <${address}>` : address;
};

const isEmailListValid = (emails: string[]): boolean =>
  emails.every((email) => EMAIL_REGEX.test(email.trim()));

const isFullHtmlDocument = (html: string): boolean => /<html[\s>]/i.test(html);

const wrapHtmlForPreview = (html: string): string => {
  if (!html.trim()) {
    return '<!DOCTYPE html><html><head></head><body></body></html>';
  }

  if (isFullHtmlDocument(html)) {
    return html;
  }

  return `<!DOCTYPE html><html><head></head><body>${html}</body></html>`;
};

const getIframeDocumentHtml = (
  doc: Document,
  preserveFullDocument: boolean
): string => {
  if (preserveFullDocument) {
    return `<!DOCTYPE html>\n${doc.documentElement.outerHTML}`;
  }

  return doc.body?.innerHTML ?? '';
};

interface EditableHtmlFieldProps {
  id?: string;
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
}

const EditableHtmlField: FC<EditableHtmlFieldProps> = ({
  id,
  value,
  onChange,
  placeholder,
  className
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const isInternalChangeRef = useRef(false);
  const lastSyncedValueRef = useRef('');
  const preserveFullDocumentRef = useRef(false);
  const hasInitializedRef = useRef(false);

  const syncIframeContent = useCallback((html: string) => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    preserveFullDocumentRef.current = isFullHtmlDocument(html);
    lastSyncedValueRef.current = html;
    iframe.srcdoc = wrapHtmlForPreview(html);
  }, []);

  const handleIframeLoad = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc?.body) return;

    doc.body.contentEditable = 'true';
    doc.body.spellcheck = false;

    const handleInput = () => {
      isInternalChangeRef.current = true;
      const html = getIframeDocumentHtml(doc, preserveFullDocumentRef.current);
      lastSyncedValueRef.current = html;
      onChange(html);
    };

    doc.addEventListener('input', handleInput);

    return () => {
      doc.removeEventListener('input', handleInput);
    };
  }, [onChange]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return undefined;

    let cleanupInputListener: (() => void) | undefined;

    const handleLoad = () => {
      cleanupInputListener?.();
      cleanupInputListener = handleIframeLoad();
    };

    iframe.addEventListener('load', handleLoad);

    return () => {
      iframe.removeEventListener('load', handleLoad);
      cleanupInputListener?.();
    };
  }, [handleIframeLoad]);

  useEffect(() => {
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return;
    }

    if (value === lastSyncedValueRef.current && hasInitializedRef.current) {
      return;
    }

    hasInitializedRef.current = true;
    syncIframeContent(value);
  }, [value, syncIframeContent]);

  return (
    <iframe id={id} ref={iframeRef} title={placeholder} className={className} />
  );
};

const SendEmailModal: FC<SendEmailModalProps> = ({
  open,
  relatedRecordId = '',
  relatedEntityType,
  recipientId = '',
  recipientEmail,
  onClose,
  onSent,
  onMinimize
}) => {
  const { email: currentUserEmail } = useRecoilValue(loginedUserDetails);
  const [selectedFromId, setSelectedFromId] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedTemplateLabel, setSelectedTemplateLabel] = useState('');
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [ccAddresses, setCcAddresses] = useState<string[]>([]);
  const defaultBccAddresses = useMemo(
    () => (currentUserEmail ? [currentUserEmail] : []),
    [currentUserEmail]
  );
  const [bccAddresses, setBccAddresses] =
    useState<string[]>(defaultBccAddresses);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const [fetchOrgWideAddresses, { data: orgWideData, loading: fromLoading }] =
    useGetSalesforceOrgWideEmailAddresses();
  const [fetchRenderedTemplate, { loading: renderingTemplate }] =
    useGetRenderedTemplateById();
  const [sendEmail, { loading: sending }] = useSendSalesforceEmail({
    // The record page that opened the draft may be gone by now, so refresh
    // whichever activity lists are mounted instead of relying on onSent.
    refetchQueries: [GET_SALESFORCE_ACTIVITIES],
    onCompleted: (data: SendSalesforceEmailResponse) => {
      const { sendSalesforceEmail } = data;
      if (sendSalesforceEmail.success) {
        showToast(
          sendSalesforceEmail.message || 'Email sent successfully',
          true
        );
        onSent?.();
        onClose();
        return;
      }

      showToast(sendSalesforceEmail.message || 'Failed to send email', false);
    },
    onError: (error: ApolloError) => {
      showToast(error.message || 'Failed to send email', false);
    }
  });

  const orgWideAddresses = useMemo(
    () => orgWideData?.getSalesforceOrgWideEmailAddresses ?? [],
    [orgWideData]
  );

  const isCcValid = useMemo(() => isEmailListValid(ccAddresses), [ccAddresses]);

  const isBccValid = useMemo(
    () => isEmailListValid(bccAddresses),
    [bccAddresses]
  );

  const canSend = useMemo(() => {
    return (
      !sending &&
      !fromLoading &&
      !renderingTemplate &&
      !!recipientEmail &&
      !!recipientId &&
      !!selectedFromId &&
      !!subject.trim() &&
      !!stripHtml(body) &&
      isCcValid &&
      isBccValid
    );
  }, [
    sending,
    fromLoading,
    renderingTemplate,
    recipientEmail,
    recipientId,
    selectedFromId,
    subject,
    body,
    isCcValid,
    isBccValid
  ]);

  // SendEmailHost mounts a fresh instance per draft, so the initial state is
  // already empty. Fetch sender addresses once; restoring from minimize must
  // not reset or refetch.
  const hasFetchedFromRef = useRef(false);
  useEffect(() => {
    if (!open || hasFetchedFromRef.current) return;

    hasFetchedFromRef.current = true;
    fetchOrgWideAddresses();
  }, [open, fetchOrgWideAddresses]);

  useEffect(() => {
    if (!open) return;
    if (selectedFromId || orgWideAddresses.length === 0) return;

    setSelectedFromId(orgWideAddresses[0].id);
  }, [open, orgWideAddresses, selectedFromId]);

  const handleTemplateSelect = (template: SalesforceEmailTemplate) => {
    setSelectedTemplateId(template.id);
    setSelectedTemplateLabel(
      template.name || template.developerName || template.id
    );
    setSubject('');
    setBody('');

    if (!recipientId) {
      showToast('Recipient is required to preview template', false);
      return;
    }

    fetchRenderedTemplate({
      variables: {
        id: template.id,
        recipientId,
        // Template rendering requires an ID; for Leads (no related record) use recipientId.
        relatedRecordId: relatedRecordId || recipientId
      }
    })
      .then(({ data }) => {
        const result = data?.getRenderedTemplateById;

        if (!result?.success || !result.rendered) {
          showToast(
            result?.message || 'Failed to load template preview',
            false
          );
          return;
        }

        setSubject(result.rendered.subject ?? '');
        setBody(result.rendered.htmlBody ?? '');
      })
      .catch((error: ApolloError) => {
        showToast(error.message || 'Failed to load template preview', false);
      });
  };

  const handleClearTemplate = () => {
    setSelectedTemplateId('');
    setSelectedTemplateLabel('');
    setSubject('');
    setBody('');
  };

  const handleClose = () => {
    setSelectedFromId('');
    setSelectedTemplateId('');
    setSelectedTemplateLabel('');
    setShowTemplatePicker(false);
    setSubject('');
    setBody('');
    onClose();
  };

  const handleSendMail = () => {
    if (!recipientEmail) {
      showToast('Recipient email is required', false);
      return;
    }
    if (!recipientId) {
      showToast('Recipient id is required', false);
      return;
    }
    if (!selectedFromId) {
      showToast('Sender address is required', false);
      return;
    }
    if (!subject.trim()) {
      showToast('Subject is required', false);
      return;
    }
    if (!stripHtml(body)) {
      showToast('Body is required', false);
      return;
    }
    if (!isCcValid) {
      showToast('Please enter valid Cc email addresses', false);
      return;
    }
    if (!isBccValid) {
      showToast('Please enter valid Bcc email addresses', false);
      return;
    }

    const selectedFrom = orgWideAddresses.find(
      (address) => address.id === selectedFromId
    );

    sendEmail({
      variables: {
        input: {
          toAddresses: [recipientEmail],
          ccAddresses: ccAddresses.length ? ccAddresses : undefined,
          bccAddresses: bccAddresses.length ? bccAddresses : undefined,
          subject,
          htmlBody: body,
          senderType: 'OrgWideEmailAddress',
          recipientId,
          ...(relatedRecordId ? { relatedRecordId } : {}),
          senderAddress: selectedFrom?.address,
          senderDisplayName: selectedFrom?.displayName,
          replyTo: selectedFrom?.address
        }
      }
    });
  };

  return (
    <ResponsiveModal
      isOpen={open}
      onClose={handleClose}
      onMinimize={onMinimize}
      title='Send Mail'
      showHeader
      className='!w-[70vw] !max-w-[70vw] !h-[80vh] flex flex-col overflow-hidden'
    >
      <div className='flex flex-1 min-h-0 gap-6 mt-4'>
        <div className='flex w-[38%] min-w-[280px] flex-col gap-4 overflow-y-auto pr-1'>
          <div className='flex flex-col'>
            <label
              htmlFor='send-email-from'
              className='text-xs text-DISABLED mb-1'
            >
              From
            </label>
            <select
              id='send-email-from'
              className='w-full border border-LIGHT_GRAY rounded-lg px-3 py-2.5 text-sm text-DEFAULT_TEXT focus:outline-none focus:border-PRIMARY'
              value={selectedFromId}
              onChange={(event) => setSelectedFromId(event.target.value)}
              disabled={fromLoading || orgWideAddresses.length === 0}
            >
              <option value=''>Select sender</option>
              {orgWideAddresses.map((address) => (
                <option key={address.id} value={address.id}>
                  {formatOrgWideAddressLabel(
                    address.displayName,
                    address.address
                  )}
                </option>
              ))}
            </select>
          </div>

          <div className='flex flex-col'>
            <label
              htmlFor='send-email-to'
              className='text-xs text-DISABLED mb-1'
            >
              To
            </label>
            <input
              id='send-email-to'
              type='text'
              value={recipientEmail}
              readOnly
              className='w-full border border-LIGHT_GRAY rounded-lg px-3 py-2.5 text-sm text-DARK_GRAY bg-GRAY5'
            />
          </div>

          <div className='flex flex-col'>
            <EmailSelectorInput
              name='send-email-cc'
              label='Cc'
              emailList={ccAddresses}
              setEmails={setCcAddresses}
              placeholder='Add Cc email and press Enter'
            />
          </div>

          <div className='flex flex-col'>
            <EmailSelectorInput
              name='send-email-bcc'
              label='Bcc'
              emailList={bccAddresses}
              setEmails={setBccAddresses}
              placeholder='Add Bcc email and press Enter'
            />
          </div>

          <div className='flex flex-col'>
            <label
              htmlFor='send-email-template'
              className='text-xs text-DISABLED mb-1'
            >
              Template
            </label>
            <div className='flex gap-2'>
              <input
                id='send-email-template'
                type='text'
                readOnly
                value={selectedTemplateLabel}
                placeholder='No template selected'
                className='flex-1 border border-LIGHT_GRAY rounded-lg px-3 py-2.5 text-sm text-DARK_GRAY bg-GRAY5'
              />
              <Button
                type='button'
                className='border border-PRIMARY text-PRIMARY text-center py-2.5 px-4 text-sm whitespace-nowrap hover:bg-PRIMARY hover:text-white transition-colors'
                onClick={() => setShowTemplatePicker(true)}
              >
                Browse
              </Button>
              <If condition={!!selectedTemplateId}>
                <Button
                  type='button'
                  className='text-DEFAULT_TEXT text-center py-2.5 px-3 text-sm whitespace-nowrap'
                  onClick={handleClearTemplate}
                >
                  Clear
                </Button>
              </If>
            </div>
          </div>

          <div className='flex flex-col'>
            <label
              htmlFor='send-email-subject'
              className='text-xs text-DISABLED mb-1'
            >
              Subject
            </label>
            <input
              id='send-email-subject'
              type='text'
              maxLength={SF_STRING_MAX_LENGTH}
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              className='w-full border border-LIGHT_GRAY rounded-lg px-3 py-2.5 text-sm text-DEFAULT_TEXT focus:outline-none focus:border-PRIMARY'
              placeholder='Enter subject'
            />
          </div>

          <If condition={fromLoading || renderingTemplate}>
            <p className='text-xs text-DISABLED'>
              {renderingTemplate
                ? 'Loading template preview...'
                : 'Loading email options...'}
            </p>
          </If>

          <div className='flex justify-end gap-3 pt-2 mt-auto'>
            <Button
              type='button'
              className='text-DEFAULT_TEXT text-center text-sm'
              onClick={handleClose}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button
              type='button'
              className='bg-PRIMARY text-white text-center py-3 px-6 text-sm disabled:opacity-50'
              onClick={handleSendMail}
              disabled={!canSend}
            >
              {sending ? 'Sending...' : 'Send'}
            </Button>
          </div>
        </div>

        <div className='flex flex-1 min-w-0 min-h-0 flex-col'>
          <label
            htmlFor='send-email-body'
            className='text-xs text-DISABLED mb-1'
          >
            Preview
          </label>
          <EditableHtmlField
            id='send-email-body'
            value={body}
            onChange={setBody}
            placeholder='Select a template to preview'
            className='flex-1 min-h-0 w-full h-full border border-LIGHT_GRAY rounded-lg bg-white focus:outline-none focus:border-PRIMARY'
          />
        </div>
      </div>

      <SalesforceEmailTemplatePickerModal
        open={showTemplatePicker}
        relatedEntityType={relatedEntityType}
        onClose={() => setShowTemplatePicker(false)}
        onSelect={handleTemplateSelect}
      />
    </ResponsiveModal>
  );
};

export default SendEmailModal;
